import { computed, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import { useMapGetter } from 'dashboard/composables/store';
import OperationalCatalogAPI from 'custom/api/operationalCatalog';

const manifestCache = new Map();

export function useOperationalCatalog(kinds = []) {
  const currentChat = useMapGetter('getSelectedChat');
  const currentAccountId = useMapGetter('getCurrentAccountId');
  const allowedIds = ref(null);
  const enabled = ref(true);
  const isLoading = ref(false);
  const loadError = ref(null);

  const conversationId = computed(() => currentChat.value?.id);
  const normalizedKinds = [...new Set(kinds)].sort();

  const loadManifest = async () => {
    allowedIds.value = null;
    loadError.value = null;

    if (!conversationId.value) {
      enabled.value = false;
      return;
    }

    const cacheKey = `${currentAccountId.value}:${conversationId.value}:${normalizedKinds.join(',')}`;
    isLoading.value = true;

    try {
      if (!manifestCache.has(cacheKey)) {
        const request = OperationalCatalogAPI.resolve({
          conversationId: conversationId.value,
          kinds: normalizedKinds,
        }).then(({ data }) => data);
        manifestCache.set(cacheKey, request);
      }

      const manifest = await manifestCache.get(cacheKey);
      enabled.value = manifest.enabled;
      allowedIds.value = manifest.allowed_ids;
    } catch (error) {
      manifestCache.delete(cacheKey);
      loadError.value = error;
      enabled.value = false;
      Sentry.captureException(error);
    } finally {
      isLoading.value = false;
    }
  };

  const filterRecords = (kind, records = []) => {
    if (!conversationId.value || !enabled.value || loadError.value) {
      return records;
    }
    if (isLoading.value || allowedIds.value === null) return [];

    const ids = new Set(allowedIds.value?.[kind] || []);
    return records.filter(record => ids.has(record.id));
  };

  watch([currentAccountId, conversationId], loadManifest, { immediate: true });

  return {
    allowedIds,
    enabled,
    filterRecords,
    isLoading,
    loadError,
    loadManifest,
  };
}
